import debounce from 'lodash.debounce';

interface PeriodicSaveOptions {
  delayMs: number;
  maxWaitMs: number;
  onError?: (error: unknown) => void;
}

type DebouncedSave = (() => void) & {
  cancel: () => void;
  flush: () => void;
};

export class PeriodicProjectTableSaveQueue<TSnapshot> {
  private pendingSnapshot: TSnapshot | null = null;
  private isSaving = false;
  private activeSavePromise: Promise<void> | null = null;
  private saveRequestedDuringInFlight = false;
  private readonly debouncedSave: DebouncedSave;

  constructor(
    private readonly saveSnapshot: (snapshot: TSnapshot) => Promise<void>,
    private readonly options: PeriodicSaveOptions
  ) {
    this.debouncedSave = debounce(
      () => {
        void this.runSave().catch(() => undefined);
      },
      options.delayMs,
      { maxWait: options.maxWaitMs }
    ) as DebouncedSave;
  }

  queue(snapshot: TSnapshot): void {
    this.pendingSnapshot = snapshot;
    this.debouncedSave();
  }

  async flush(): Promise<void> {
    if (!this.pendingSnapshot && !this.activeSavePromise) {
      return;
    }

    this.debouncedSave.flush();
    if (this.activeSavePromise) {
      await this.activeSavePromise;
    }

    while (this.pendingSnapshot) {
      this.debouncedSave.cancel();
      await this.runSave();
      if (this.activeSavePromise) {
        await this.activeSavePromise;
      }
    }
  }

  cancel(): void {
    this.debouncedSave.cancel();
    this.pendingSnapshot = null;
    this.saveRequestedDuringInFlight = false;
  }

  hasPending(): boolean {
    return Boolean(this.pendingSnapshot || this.activeSavePromise);
  }

  private async runSave(): Promise<void> {
    if (this.isSaving) {
      this.saveRequestedDuringInFlight = true;
      if (this.activeSavePromise) {
        await this.activeSavePromise;
      }
      return;
    }

    const snapshot = this.pendingSnapshot;
    if (!snapshot) {
      return;
    }

    this.pendingSnapshot = null;
    this.isSaving = true;
    const savePromise = this.saveSnapshot(snapshot);
    this.activeSavePromise = savePromise;

    try {
      await savePromise;
    } catch (error) {
      if (!this.pendingSnapshot) {
        this.pendingSnapshot = snapshot;
      }
      this.options.onError?.(error);
      throw error;
    } finally {
      if (this.activeSavePromise === savePromise) {
        this.activeSavePromise = null;
      }
      this.isSaving = false;
      if (this.saveRequestedDuringInFlight || this.pendingSnapshot) {
        this.saveRequestedDuringInFlight = false;
        this.debouncedSave();
      }
    }
  }
}
